import React, { useState, useEffect } from "react";
import { Box, Typography, Button, TextField, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, CircularProgress, Tooltip, Avatar, Pagination } from "@mui/material";
import { ToastContainer, toast } from 'react-toastify';
import { Edit as EditIcon, Delete as DeleteIcon, AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { createDepartment, updateDepartment, deleteDepartment, getDepartmentByID } from "../../api/department-api";
import { getSubdepartment, createSubDepartment, updateSubDepartment, deleteSubDepartment } from "../../api/subDepartment-api";
import { useSelector } from "react-redux";
import _ from "lodash";
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from "../../firebaseconfig";
import { DepartmentDialog } from "../departments/departmentDialog";
import { SubDepartmentDialog } from "../departments/subDepartmentDialog";
import TypesOfSubDepartment from "../TypesOfSubDepartment";

const UserPostManagement = () => {
  const adminId = useSelector((state) => state?.users?.id);
  const [departments, setDepartments] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [subDepartments, setSubDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [openDepartmentDialog, setOpenDepartmentDialog] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [departmentDialogData, setDepartmentDialogData] = useState({ id: null, name: "", description: "", image: "", author: '', adminId: adminId, });
  const [openSubDepartmentDialog, setOpenSubDepartmentDialog] = useState(false);
  const [subDepartmentDialogData, setSubDepartmentDialogData] = useState({ id: selectedDepartment, name: "", description: "", definition: "", iconImage: "", image: "", author: '', pdfUrl: "", });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const data = await getDepartmentByID(adminId);
        setDepartments(data);
        setFilteredDepartments(data);
      } catch (error) {
        showSnackbar("Error fetching departments.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, [adminId]);

  useEffect(() => {
    if (selectedDepartment) {
      const fetchSubDepartments = async () => {
        setLoading(true);
        try {
          const data = await getSubdepartment(selectedDepartment);
          setSubDepartments(data);
          setFilteredSubDepartments(data);
        } catch (error) {
          showSnackbar("Error fetching sub-departments.", "error");
        } finally {
          setLoading(false);
        }
      };
      fetchSubDepartments();
    }
  }, [selectedDepartment]);


  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setDepartmentSearchTerm(searchValue);

    const filtered = departments.filter((dept) =>
      dept.name.toString() === searchValue
    );
    setFilteredDepartments(filtered);

    if (!filtered.length) {
      console.log('Data not found');
    }
  };

  const handleSelectDepartment = (departmentId) => {
    setSelectedDepartment(departmentId);
  };

  const handleOpenDepartmentDialog = (department) => {
    setDepartmentDialogData(department || { id: null, name: "", description: "", image: "", author: '', });
    setOpenDepartmentDialog(true);
  };

  const handleCloseDepartmentDialog = () => {
    setOpenDepartmentDialog(false);
    setDepartmentDialogData({ id: null, name: "", description: "", image: "", author: '', });
  };

  const handleOpenSubDepartmentDialog = () => {
    setOpenSubDepartmentDialog(true);
  };

  const handleCloseSubDepartmentDialog = () => {
    setOpenSubDepartmentDialog(false);
    setSubDepartmentDialogData({ id: null, name: "", description: "", iconImage: "", image: "", author: '', defination: '', pdfUrl: '' });
  };

  const handleSaveDepartment = async () => {
    setLoading(true);

    const dataToSave = {
      ...departmentDialogData,
      adminId: adminId,
    };

    try {
      if (dataToSave.id) {
        await updateDepartment(dataToSave.id, dataToSave);
        setDepartments((prevDepartments) =>
          prevDepartments.map((dept) =>
            dept.id === dataToSave.id ? dataToSave : dept
          )
        );
        toast("Department updated successfully.");
      } else {
        const newDepartment = await createDepartment(dataToSave);
        setDepartments((prevDepartments) => [...prevDepartments, newDepartment]);
        toast("Department added successfully.");
      }
      handleCloseDepartmentDialog();
    } catch (error) {
      showSnackbar("Failed to save department.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubDepartment = async () => {
    setLoading(true);

    const dataToSave = {
      ...subDepartmentDialogData,
      departmentId: selectedDepartment,
    };
    console.log('dataToSave========>', dataToSave);

    try {
      if (dataToSave.id) {
        await updateSubDepartment(dataToSave.id, dataToSave);
        setSubDepartments((prevSubDepartments) => {
          console.log('Previous SubDepartments:', prevSubDepartments);
          return prevSubDepartments.map((subDept) =>
            subDept.id === dataToSave.id ? dataToSave : subDept
          );
        });
        toast("Sub-Department updated successfully.");
      } else {
        const newSubDepartment = await createSubDepartment(dataToSave);
        setSubDepartments((prevSubDepartments) => [...prevSubDepartments, newSubDepartment]);
        toast("Sub-Department added successfully.");
        handleCloseSubDepartmentDialog();
      }
    } catch (error) {
      showSnackbar("Failed to save sub-department.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    setLoading(true);
    try {
      if (window.confirm("Are you sure you want to delete this department?")) {
        await deleteDepartment(id);
        setDepartments((prevDepartments) =>
          prevDepartments.filter((dept) => dept.id !== id)
        );
        toast("Department deleted successfully.");
        setSelectedDepartment(null);
        setSubDepartments([]);
      }
    } catch (error) {
      showSnackbar("Failed to delete department.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubDepartment = async (id) => {
    setLoading(true);
    try {
      if (
        window.confirm("Are you sure you want to delete this sub-department?")
      ) {
        await deleteSubDepartment(id);
        setSubDepartments((prevSubDepartments) =>
          prevSubDepartments.filter((subDept) => subDept.id !== id)
        );
        toast("Sub-Department deleted successfully.");
      }
    } catch (error) {
      showSnackbar("Failed to delete sub-department.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedDepartments = filteredDepartments.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      const storageRef = ref(storage, `images/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
          setImageUploading(true);
        },
        (error) => {
          console.error('Error uploading image:', error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setSubDepartmentDialogData(prev => ({ ...prev, image: downloadURL }));
            setImageUploading(false);
            toast("Image uploaded successfully:", downloadURL);
          });
        }
      );
    }
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubDepartmentDialogData({ ...subDepartmentDialogData, pdf: URL.createObjectURL(file) });
    }
  };

  return (
    <Box sx={{ p: 4, m: 5 }}>
      <Typography
        variant="h4"
        gutterBottom
        justifyContent={"center"}
        textAlign={"center"}
      >
        Your Departments
      </Typography>

      <TextField
        label="Search Departments"
        variant="outlined"
        fullWidth
        value={departmentSearchTerm}
        onChange={handleSearch}
        sx={{ mb: 2 }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleOpenDepartmentDialog()}
        sx={{ mb: 2 }}
      >
        <AddCircleOutlineIcon sx={{ mr: 1 }} />
        Add Department
      </Button>

      <TableContainer component={Paper} elevation={8} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#3f51b5", color: "#fff" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Title</strong>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Description</strong>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Image</strong>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Author</strong>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Created At</strong>
              </TableCell>
              <TableCell sx={{ color: "#fff" }}>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              paginatedDepartments.map((department) => (
                <TableRow key={department.id}>
                  <TableCell>{department.name}</TableCell>
                  <TableCell>{department.description}</TableCell>
                  <TableCell>
                    {department.image && (
                      <Avatar
                        src={department.image}
                        alt={department.name}
                        sx={{ width: 56, height: 56 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{department.author}</TableCell>
                  <TableCell>{department.createdAt}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDepartmentDialog(department)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="secondary"
                        onClick={() => handleDeleteDepartment(department.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      onClick={() => handleSelectDepartment(department.id)}
                      sx={{ ml: 1 }}
                    >
                      View Sub-Departments
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        count={Math.ceil(filteredDepartments.length / itemsPerPage)}
        page={page}
        onChange={handleChangePage}
        sx={{ mb: 2 }}
      />

      {selectedDepartment && (
        <Box>
          <Typography
            variant="h5"
            gutterBottom
            textAlign="center"
          >
            Sub-Departments
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenSubDepartmentDialog}
            sx={{ mb: 2 }}
          >
            <AddCircleOutlineIcon sx={{ mr: 1 }} />
            Add Sub-Department
          </Button>

          <TableContainer
            component={Paper}
            elevation={8}
            sx={{ borderRadius: 2 }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#3f51b5", color: "#fff" }}>
                <TableRow>
                  {["Title", "Description", "Definition", "Author", "Created At", "Image", "Icons", "PDF", "Actions"].map((header) => (
                    <TableCell key={header} sx={{ color: "#fff" }}>
                      <strong>{header}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubDepartments.length > 0 ? (
                    filteredSubDepartments.map((subDepartment) => (
                      <TableRow key={subDepartment.id}>
                        <TableCell>{subDepartment.name}</TableCell>
                        <TableCell>{subDepartment.description}</TableCell>
                        <TableCell>{subDepartment.definition}</TableCell>
                        <TableCell>{subDepartment.author}</TableCell>
                        <TableCell>{subDepartment.createdAt}</TableCell>
                        <TableCell>
                          {subDepartment.image && (
                            <Avatar
                              src={subDepartment.image}
                              alt={subDepartment.name}
                              sx={{ width: 56, height: 56 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {subDepartment.iconImage && (
                            <Avatar
                              src={subDepartment.iconImage}
                              alt={subDepartment.name}
                              sx={{ width: 56, height: 56 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {subDepartment.pdfUrl ? (
                            <a href={subDepartment.pdfUrl} target="_blank" rel="noopener noreferrer">
                              View PDF
                            </a>
                          ) : (
                            "No PDF available"
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenSubDepartmentDialog(subDepartment)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="secondary"
                              onClick={() => handleDeleteSubDepartment(subDepartment.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No sub-departments found.
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Departments add */}
      <DepartmentDialog openDepartmentDialog={openDepartmentDialog} handleCloseDepartmentDialog={handleCloseDepartmentDialog}
        departmentDialogData={departmentDialogData} setDepartmentDialogData={setDepartmentDialogData} handleSaveDepartment={handleSaveDepartment}
        handleImageUpload={handleImageUpload} imageUploading={imageUploading} />
      {/* subDepartment add */}
      <SubDepartmentDialog openSubDepartmentDialog={openSubDepartmentDialog} handleCloseSubDepartmentDialog={handleCloseSubDepartmentDialog}
        subDepartmentDialogData={subDepartmentDialogData} setSubDepartmentDialogData={setSubDepartmentDialogData} handlePdfUpload={handlePdfUpload} handleImageUpload={handleImageUpload}
        departmentDialogData={departmentDialogData} imageUploading={imageUploading}
        handleSaveSubDepartment={handleSaveSubDepartment} />
      <TypesOfSubDepartment filteredSubDepartments={filteredSubDepartments} />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ToastContainer />
    </Box>
  );
};

export default UserPostManagement;
